/**
 * Custom Element pour le modal alerte-site (formulaire single-page)
 */
class AlertSiteModal extends HTMLElement {
  static FILTER_PARAM_MAP = {
    type: 'filter.p.m.specs.category',
    category: 'filter.p.m.velo.categorie_secondaire',
  };

  // La marque est proposée comme filtre supplémentaire (via "Ajouter un filtre")
  static MARQUE_FILTER_ID = 'filter.p.m.specs.mar';

  // Catégories affichées par défaut (dans cet ordre) ; le reste est masqué derrière "Voir plus"
  static PRIMARY_CATEGORY_LABELS = ['VTT', 'GRAVEL', 'ROUTE'];

  // Bornes acceptées pour les champs budget (€)
  static BUDGET_BOUNDS = { min: 500, max: 20000 };

  // Bornes acceptées pour la taille du cycliste (cm). Lues par la validation ET par
  // le pré-remplissage : deux copies en dur laisseraient le prefill injecter une
  // valeur que la validation refuse, sur un champ que le visiteur n'a pas rempli.
  static TAILLE_BOUNDS = { min: 100, max: 250 };

  static parseFilterInputValues(input) {
    try {
      const parsed = typeof input === 'string' ? JSON.parse(input) : input;
      const entries = Array.isArray(parsed) ? parsed : [parsed];
      const values = [];
      for (const entry of entries) {
        const val = entry.productMetafield?.value || entry.variantMetafield?.value;
        if (val && !val.startsWith('gid://')) values.push(val);
      }
      return values.length > 0 ? values.join(', ') : null;
    } catch {
      return null;
    }
  }

  // Transforme les valeurs d'un filtre en items {label, rawValues} triés par label
  static buildItems(filter) {
    return filter.values
      .map((v) => ({ label: v.label, rawValues: AlertSiteModal.parseFilterInputValues(v.input) || v.label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  static EXCLUDED_FILTER_IDS = new Set([
    'filter.p.m.specs.type',
    'filter.p.m.specs.category',
    'filter.p.m.sync.velo_type_type',
    'filter.p.m.specs.taicyc',
    'filter.v.m.taille_du_cycliste.liste',
    'filter.v.price',
    'filter.v.availability',
    'filter.p.m.velo.categorie_secondaire',
    'filter.p.m.filter.en_promo',
  ]);

  constructor() {
    super();

    this.emailValidationTimeout = null;
    this.isSubmitting = false;
    this.submitted = false;

    this.collectionFilters = null;
    this.filtersLoaded = false;
    this.addedFilters = new Set();

    // Déclencheur ayant ouvert la modale à l'ouverture courante (ex. CTA "Me prévenir"
    // d'un vélo vendu, cf. applyTriggerPrefill). null pour les 7 autres surfaces qui
    // ouvrent cette modale sans porter de data-vvr-sold-* : le pré-remplissage reste opt-in.
    this.pendingPrefillTrigger = null;
    // Pré-remplissage programmatique en attente (cf. prefill()) — consommé une seule fois
    this.prefillData = null;

    this.handleClick = this.handleClick.bind(this);
    this.handleModalClosed = this.handleModalClosed.bind(this);
    this.handleModalOpened = this.handleModalOpened.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('#x-bike-alert');
    this.viewForm = this.querySelector('[data-view="form"]');
    this.viewSuccess = this.querySelector('[data-view="success"]');

    this.initializeEventListeners();
    this.initializeInputSanitization();
    this.initMultiselects();

    // Perf : les filtres ne sont chargés qu'à l'ouverture de la modale (cf. handleModalOpened),
    // pas à chaque page. Cas particulier : modale déjà ouverte au montage (auto-open via ?modal=true).
    const xModal = this.closest('x-modal');
    if (xModal && xModal.isOpen) this.loadDropdownValues();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('modal-closed', this.handleModalClosed);
    document.removeEventListener('modal-opened', this.handleModalOpened);
    document.removeEventListener('click', this.handleOutsideClick);
  }

  // ---------------------------------------------------------------------------
  // Event listeners
  // ---------------------------------------------------------------------------

  initializeEventListeners() {
    this.addEventListener('click', this.handleClick);
    document.addEventListener('modal-closed', this.handleModalClosed);
    document.addEventListener('modal-opened', this.handleModalOpened);

    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSubmit();
      });
    }

    // Real-time validation
    this.addEventListener('input', (e) => {
      const target = e.target;
      if (target.name === 'email' && target.type === 'email') {
        this.validateEmail(target);
        this.toggleMintFields(target.value);
      }
      // Clear required-field errors on input
      if (target.name === 'taille') {
        if (target.value.trim()) {
          target.classList.remove('error');
          const errorEl = this.querySelector(`#${target.name}-error`);
          if (errorEl) {
            errorEl.classList.remove('show');
            errorEl.textContent = '';
          }
        }
      }
      // Budget : min et max partagent le même message d'erreur
      if (target.name === 'budget_min' || target.name === 'budget_max') {
        this.clearBudgetError();
      }
    });
  }

  handleClick(event) {
    const target = event.target;

    // Close button (X)
    if (target.closest('.alerte-site-modal__close-button')) {
      this.handleCloseClick();
      return;
    }

    // Close success button
    if (target.closest('[data-action="close"]')) {
      this.handleCloseClick();
      return;
    }

    // New alert button (reset form)
    if (target.closest('[data-action="new-alert"]')) {
      // Le trigger est consomme : sans ca, resetForm() re-applique le
      // pre-remplissage du velo dont l'alerte vient d'etre soumise, et le
      // visiteur repart avec exactement les memes criteres — donc un doublon
      // s'il ne le remarque pas.
      this.pendingPrefillTrigger = null;
      this.resetForm();
      return;
    }

    // Toggle: masquer les filtres optionnels vides / sinon ajouter tous les filtres.
    // Les filtres qui ont une valeur restent toujours affichés.
    if (target.closest('[data-action="add-filter"]')) {
      if (this.hasRemovableDynamicFilters()) {
        this.removeEmptyDynamicFilters();
      } else {
        this.addAllDynamicFilters();
      }
      this.updateAddFilterButton();
      return;
    }
  }

  handleModalClosed(event) {
    // Ne réagir qu'à la fermeture de CETTE modale (pas les autres modales du thème)
    const myModalId = this.closest('x-modal')?.dataset.modalId;
    if (!myModalId || event.detail?.modalId !== myModalId) return;

    this.track('alerte_site_fermeture', { alerte_soumise: this.submitted });
    // Un prefill non consommé (fermeture pendant le chargement des filtres) ne doit pas
    // ressurgir à une ouverture ultérieure depuis un autre point d'entrée.
    this.prefillData = null;
    this.resetForm();
  }

  handleModalOpened(event) {
    const myModalId = this.closest('x-modal')?.dataset.modalId;
    if (!myModalId || event.detail?.modalId !== myModalId) return;

    // Rafraîchi à CHAQUE ouverture (jamais de fuite d'un vélo à l'autre) : le détail
    // ne porte le trigger que si x-modal.js l'a résolu (cf. XModal._handleDocumentClick).
    this.pendingPrefillTrigger = event.detail?.trigger || null;

    if (!this.filtersLoaded) {
      // 1re ouverture : charger les filtres (le fetch se fait ici, pas au chargement de la page).
      // loadDropdownValues finit par la pré-sélection depuis l'URL de collection.
      this.loadDropdownValues();
    } else {
      // Ouvertures suivantes : resynchroniser avec les filtres de collection courants
      // (le filtrage est en AJAX, l'URL change sans recharger → on relit l'URL à chaque ouverture).
      this.resetForm();
    }
  }

  handleCloseClick() {
    const modal = this.closest('x-modal');
    if (modal && typeof modal.close === 'function') {
      modal.close();
    } else if (modal) {
      const nativeCloseBtn = modal.querySelector('.x-modal-close');
      if (nativeCloseBtn) nativeCloseBtn.click();
    }
  }

  // ---------------------------------------------------------------------------
  // Input sanitization (budget & taille: digits only)
  // ---------------------------------------------------------------------------

  initializeInputSanitization() {
    ['taille', 'budget_min', 'budget_max'].forEach((name) => {
      const input = this.querySelector(`input[name="${name}"]`);
      if (!input) return;
      input.addEventListener('input', () => {
        input.value = input.value.replace(/[^\d]/g, '');
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Budget error helpers (min & max partagent le span #budget-error)
  // ---------------------------------------------------------------------------

  clearBudgetError() {
    ['budget_min', 'budget_max'].forEach((name) => {
      this.querySelector(`input[name="${name}"]`)?.classList.remove('error');
    });
    const errorEl = this.querySelector('#budget-error');
    if (errorEl) {
      errorEl.classList.remove('show');
      errorEl.textContent = '';
    }
  }

  showBudgetError(message, inputs) {
    inputs.forEach((input) => input?.classList.add('error'));
    const errorEl = this.querySelector('#budget-error');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.add('show');
    }
  }

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  validateEmail(input) {
    const errorElement = this.querySelector('#email-error');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (this.emailValidationTimeout) {
      clearTimeout(this.emailValidationTimeout);
    }

    input.classList.remove('error');
    if (errorElement) {
      errorElement.classList.remove('show');
      errorElement.textContent = '';
    }

    if (input.value.length === 0) {
      input.setCustomValidity('');
      return;
    }

    if (!emailRegex.test(input.value)) {
      const errorMessage = 'Veuillez saisir une adresse email valide';
      input.setCustomValidity(errorMessage);
      input.classList.add('error');
      if (errorElement) {
        this.emailValidationTimeout = setTimeout(() => {
          const currentValue = input.value.trim();
          if (!emailRegex.test(currentValue)) {
            errorElement.textContent = errorMessage;
            errorElement.classList.add('show');
          }
        }, 500);
      }
    } else {
      input.setCustomValidity('');
    }
  }

  // ---------------------------------------------------------------------------
  // MINT internal fields
  // ---------------------------------------------------------------------------

  toggleMintFields(email) {
    const mintFields = this.querySelector('[data-mint-fields]');
    if (!mintFields) return;
    const isMint = email.trim().toLowerCase().endsWith('@mint-bikes.com');
    const wasHidden = mintFields.style.display === 'none';
    mintFields.style.display = isMint ? '' : 'none';
    if (isMint && wasHidden) {
      const wrapper = mintFields.querySelector('[data-mint-message]');
      if (wrapper) {
        wrapper.classList.remove('animate-in', 'collapse');
        wrapper.style.height = '';
        void wrapper.offsetWidth;
        wrapper.classList.add('animate-in');
        const bike = wrapper.querySelector('.alerte-site-modal__mint-bike');
        if (bike) {
          bike.addEventListener(
            'animationend',
            () => {
              wrapper.style.height = wrapper.offsetHeight + 'px';
              void wrapper.offsetWidth;
              wrapper.classList.add('collapse');
            },
            { once: true },
          );
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Dynamic filters (collection-driven)
  // ---------------------------------------------------------------------------

  getAvailableDynamicFilters() {
    if (!this.collectionFilters) return [];
    return Object.values(this.collectionFilters).filter((f) => {
      if (f.type !== 'LIST') return false;
      if (AlertSiteModal.EXCLUDED_FILTER_IDS.has(f.id)) return false;
      if (this.addedFilters.has(f.id)) return false;
      // Exclure les filtres avec des valeurs groupées (GID)
      const hasGrouped = f.values.some((v) => {
        const raw = AlertSiteModal.parseFilterInputValues(v.input);
        return raw === null;
      });
      if (hasGrouped) return false;
      return true;
    });
  }

  addAllDynamicFilters() {
    const remaining = this.getAvailableDynamicFilters();
    if (remaining.length === 0) return;

    for (const filter of remaining) {
      this.addDynamicFilter(filter);
    }
  }

  // Y a-t-il des filtres supplémentaires affichés SANS valeur (donc masquables) ?
  hasRemovableDynamicFilters() {
    return Array.from(this.querySelectorAll('[data-dynamic-filter]')).some(
      (el) => !this.getMultiselectValues(el.dataset.dynamicFilter),
    );
  }

  // "Masquer les filtres" : retire uniquement les filtres SANS valeur.
  // Ceux qui ont une valeur sélectionnée restent affichés.
  removeEmptyDynamicFilters() {
    this.querySelectorAll('[data-dynamic-filter]').forEach((el) => {
      const key = el.dataset.dynamicFilter;
      if (this.getMultiselectValues(key)) return;
      el.remove();
      this.addedFilters.delete(key);
    });
  }

  // Met à jour le libellé / l'état du bouton "Ajouter un filtre" (toggle)
  updateAddFilterButton() {
    const addBtn = this.querySelector('[data-action="add-filter"]');
    if (!addBtn) return;

    const hasRemovable = this.hasRemovableDynamicFilters();
    const hasAvailable = this.getAvailableDynamicFilters().length > 0;

    // Rien à masquer et rien à ajouter → masquer le bouton
    addBtn.style.display = !hasRemovable && !hasAvailable ? 'none' : '';

    addBtn.classList.toggle('alerte-site-modal__add-filter--active', hasRemovable);
    const labelEl = addBtn.querySelector('span');
    if (labelEl) labelEl.textContent = hasRemovable ? 'Masquer les filtres' : 'Ajouter un filtre';
  }

  addDynamicFilter(filter) {
    this.addedFilters.add(filter.id);

    const addBtn = this.querySelector('[data-action="add-filter"]');
    const formGroup = document.createElement('div');
    formGroup.className = 'alerte-site-modal__form-group alerte-site-modal__dynamic-filter';
    formGroup.dataset.dynamicFilter = filter.id;

    const labelEl = document.createElement('label');
    labelEl.textContent = filter.label;
    formGroup.appendChild(labelEl);

    const msName = filter.id;
    const msDiv = document.createElement('div');
    msDiv.className = 'alerte-site-modal__multiselect';
    msDiv.dataset.multiselect = msName;
    msDiv.innerHTML = `
      <div class="alerte-site-modal__multiselect-trigger" aria-expanded="false" data-empty="true">
        <input type="text" class="alerte-site-modal__multiselect-input"
          placeholder="Choisir\u2026" data-placeholder="Choisir\u2026" autocomplete="off">
        <svg class="alerte-site-modal__multiselect-chevron" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1.5l5 5 5-5" stroke="#141414" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="alerte-site-modal__multiselect-panel" hidden>
        <ul class="alerte-site-modal__multiselect-options"></ul>
      </div>`;
    formGroup.appendChild(msDiv);

    if (addBtn) addBtn.before(formGroup);

    this.bindMultiselectEvents(msDiv);

    // Populate immediately — values are already available
    this.populateMultiselect(msName, AlertSiteModal.buildItems(filter));

    this.updateAddFilterButton();
  }

  // ---------------------------------------------------------------------------
  // Tracking (dataLayer / GTM)
  // ---------------------------------------------------------------------------

  // Passe par le helper maison window.trackEvent si présent, sinon push direct
  track(event, data) {
    if (typeof window.trackEvent === 'function') return window.trackEvent(event, data);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event }, data));
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async handleSubmit() {
    if (this.isSubmitting) return;

    this.track('alerte_site_clic', {
      cta_name: 'creer_alerte',
      alerte_type: this.getMultiselectValues('type'),
      alerte_category: this.getMultiselectValues('category'),
      alerte_marque: this.getMultiselectValues(AlertSiteModal.MARQUE_FILTER_ID),
      alerte_taille: this.querySelector('input[name="taille"]')?.value || '',
      alerte_budget_min: this.querySelector('input[name="budget_min"]')?.value || '',
      // `alerte_budget` reste la borne max (continuité des rapports existants)
      alerte_budget: this.querySelector('input[name="budget_max"]')?.value || '',
    });

    const formData = new FormData(this.form);

    const emailInput = this.querySelector('input[name="email"]');

    // Validate email if filled
    if (emailInput && emailInput.value.trim()) {
      this.validateEmail(emailInput);
      if (!emailInput.validity.valid) {
        this.track('alerte_site_error', { alerte_reason: 'email_invalide' });
        return;
      }
    }

    // Email requis
    const hasEmail = emailInput && emailInput.value.trim();
    if (!hasEmail) {
      const emailError = this.querySelector('#email-error');
      if (emailError) {
        emailError.textContent = 'Veuillez renseigner un email';
        emailError.classList.add('show');
      }
      this.track('alerte_site_error', { alerte_reason: 'contact_manquant' });
      return;
    }

    // Validate required fields: type, category, taille & budget
    const tailleInput = this.querySelector('input[name="taille"]');
    const invalidFields = [];

    ['type', 'category'].forEach((name) => {
      const ms = this.querySelector(`[data-multiselect="${name}"]`);
      if (!ms) return;
      const value = this.getMultiselectValues(name);
      if (!value) {
        ms.classList.add('error');
        const errorEl = this.querySelector(`#${name}-error`);
        if (errorEl) {
          errorEl.textContent =
            name === 'type' ? 'Veuillez sélectionner un type de vélo' : 'Veuillez sélectionner une catégorie';
          errorEl.classList.add('show');
        }
        invalidFields.push(name);
      }
    });

    if (tailleInput) {
      const { min: TAILLE_LOW, max: TAILLE_HIGH } = AlertSiteModal.TAILLE_BOUNDS;
      const tailleVal = parseInt(tailleInput.value.trim(), 10);
      if (!tailleInput.value.trim() || isNaN(tailleVal) || tailleVal < TAILLE_LOW || tailleVal > TAILLE_HIGH) {
        tailleInput.classList.add('error');
        const tailleError = this.querySelector('#taille-error');
        if (tailleError) {
          tailleError.textContent = !tailleInput.value.trim()
            ? 'Veuillez renseigner votre taille'
            : `La taille doit être comprise entre ${TAILLE_LOW} et ${TAILLE_HIGH} cm`;
          tailleError.classList.add('show');
        }
        invalidFields.push('taille');
      }
    }

    // Budget min & max optionnels : chaque borne n'est validée que si elle est renseignée
    const budgetMinInput = this.querySelector('input[name="budget_min"]');
    const budgetMaxInput = this.querySelector('input[name="budget_max"]');
    const budgetMinRaw = budgetMinInput?.value.trim() || '';
    const budgetMaxRaw = budgetMaxInput?.value.trim() || '';
    const budgetMinVal = budgetMinRaw ? parseInt(budgetMinRaw, 10) : null;
    const budgetMaxVal = budgetMaxRaw ? parseInt(budgetMaxRaw, 10) : null;

    const { min: BUDGET_LOW, max: BUDGET_HIGH } = AlertSiteModal.BUDGET_BOUNDS;
    const isOutOfBounds = (val) => val !== null && (isNaN(val) || val < BUDGET_LOW || val > BUDGET_HIGH);

    const outOfBounds = [];
    if (isOutOfBounds(budgetMinVal)) outOfBounds.push(budgetMinInput);
    if (isOutOfBounds(budgetMaxVal)) outOfBounds.push(budgetMaxInput);

    if (outOfBounds.length > 0) {
      this.showBudgetError('Le budget doit être compris entre 500 et 20 000 €', outOfBounds);
      invalidFields.push('budget');
    } else if (budgetMinVal !== null && budgetMaxVal !== null && budgetMinVal > budgetMaxVal) {
      this.showBudgetError('Le budget minimum ne peut pas dépasser le budget maximum', [
        budgetMinInput,
        budgetMaxInput,
      ]);
      invalidFields.push('budget');
    }

    if (invalidFields.length > 0) {
      this.track('alerte_site_error', {
        alerte_reason: 'champs_requis',
        alerte_champs: invalidFields.join(','),
      });
      return;
    }

    const tailleRaw = formData.get('taille') || '';
    const taille = tailleRaw ? `${tailleRaw}` : '';

    const ts = Date.now().toString(36);
    const rand = Math.random().toString(36).substring(2, 10);
    const alertId = `alrt_${ts}_${rand}`;

    const data = {
      id: alertId,
      firstname: formData.get('firstname') || '',
      lastname: formData.get('lastname') || '',
      email: formData.get('email') || '',
      phone: '',
      type: this.getMultiselectValues('type'),
      category: this.getMultiselectValues('category'),
      taille,
      marque: this.getMultiselectValues(AlertSiteModal.MARQUE_FILTER_ID),
      budget_min: budgetMinRaw,
      budget_max: budgetMaxRaw,
    };

    // MINT internal fields
    const clientEmail = formData.get('client_email') || '';
    const note = formData.get('note') || '';
    if (clientEmail) data._client_email = clientEmail;
    if (note) data._note = note;

    this.isSubmitting = true;
    const submitBtn = this.querySelector('.alerte-site-modal__submit');
    if (submitBtn) submitBtn.disabled = true;

    try {
      await this.submitToAPI(data);
      this.submitted = true;

      this.track('alerte_site_validation', {
        alerte_id: data.id,
        alerte_type: data.type,
        alerte_category: data.category,
        alerte_marque: data.marque,
        alerte_taille: data.taille,
        alerte_budget_min: data.budget_min,
        alerte_budget: data.budget_max,
      });

      this.showSuccess();
    } catch (error) {
      console.error('[alerte-site-modal] Submit error:', error);
      this.track('alerte_site_submit_error', { alerte_id: data.id });
      this.showSubmitError();
    } finally {
      this.isSubmitting = false;
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------------

  async submitToAPI(data) {
    const apiUrl = 'https://mintbikes.app.n8n.cloud/webhook/alerte_site';

    const params = {};

    // Multiselect fields (type, category, marque + dynamic filters)
    this.querySelectorAll('[data-multiselect]').forEach((ms) => {
      const name = ms.dataset.multiselect;
      const value = this.getMultiselectValues(name);
      if (!value) return;
      const mapped = AlertSiteModal.FILTER_PARAM_MAP[name];
      // type/category → clé via FILTER_PARAM_MAP, `name` déjà lisible (ex. "type")
      // filtres additionnels → `name` est l'ID de filtre : clé = ID sans préfixe, name = libellé en minuscules
      const key = (mapped || name).replace(/^filter\./, '');
      const paramName = mapped ? name : (this.collectionFilters?.[name]?.label || name).toLowerCase();
      params[key] = { name: paramName, value };
    });

    // Text/number fields
    if (data.taille) params.size = { name: 'taille', value: data.taille };

    // Budget → bornes du filtre prix Shopify.
    // Le min est toujours transmis (0 quand il n'est pas renseigné) ; le max seulement s'il l'est.
    params['v.price.gte'] = { name: 'budget_min', value: data.budget_min || '0' };
    if (data.budget_max) params['v.price.lte'] = { name: 'budget_max', value: data.budget_max };

    const payload = {
      id: data.id,
      firstName: data.firstname,
      lastName: data.lastname,
      email: data.email,
      phone: data.phone,
      params,
    };

    if (data._client_email) payload._client_email = data._client_email;
    if (data._note) payload._note = data._note;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  }

  // ---------------------------------------------------------------------------
  // Storefront API — collection filters
  // ---------------------------------------------------------------------------

  async fetchCollectionFilters() {
    const token = this.dataset.storefrontToken;
    if (!token) return null;

    const query = `{
      collection(handle: "globofilter-best-selling-products-index") {
        products(first: 1) {
          filters {
            id
            label
            type
            values {
              label
              count
              input
            }
          }
        }
      }
    }`;

    const res = await fetch('https://velokaz.myshopify.com/api/2026-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query }),
    });

    const json = await res.json();
    const filters = json.data?.collection?.products?.filters || [];

    this.collectionFilters = {};
    for (const f of filters) {
      this.collectionFilters[f.id] = f;
    }

    return this.collectionFilters;
  }

  async loadDropdownValues() {
    this.filtersLoaded = true;
    await this.fetchCollectionFilters();

    const typeFilter = this.collectionFilters?.[AlertSiteModal.FILTER_PARAM_MAP.type];
    if (typeFilter) {
      this.populateButtonGroup('type', AlertSiteModal.buildItems(typeFilter));
    }

    const categoryFilter = this.collectionFilters?.[AlertSiteModal.FILTER_PARAM_MAP.category];
    if (categoryFilter) {
      this.populateButtonGroup('category', AlertSiteModal.buildItems(categoryFilter), {
        primaryLabels: AlertSiteModal.PRIMARY_CATEGORY_LABELS,
      });
    }

    // Remove loading state from button groups
    this.querySelectorAll('.alerte-site-modal__button-group--loading').forEach((group) => {
      group.classList.remove('alerte-site-modal__button-group--loading');
    });

    // Masquer le bouton "Ajouter un filtre" s'il n'y a aucun filtre dynamique disponible
    this.updateAddFilterButton();

    // Pré-remplir depuis les filtres actifs de la collection (si ouverte depuis une collection filtrée)
    this.preselectFromCollectionFilters();

    // Pré-remplir depuis le déclencheur (ex. CTA "Me prévenir" d'un vélo vendu) : opt-in,
    // n'a d'effet que si le trigger porte des data-vvr-sold-*.
    this.applyTriggerPrefill();
  }

  // ---------------------------------------------------------------------------
  // Pré-sélection depuis les filtres de la collection (URL)
  // ---------------------------------------------------------------------------

  preselectFromCollectionFilters() {
    if (!this.collectionFilters) return;
    const params = new URLSearchParams(window.location.search);

    const filterKeys = new Set(Array.from(params.keys()).filter((k) => k.startsWith('filter.')));
    if (filterKeys.size > 0) {
      // Type & catégorie (button-groups déjà peuplés)
      for (const [name, filterId] of Object.entries(AlertSiteModal.FILTER_PARAM_MAP)) {
        params.getAll(filterId).forEach((value) => this.checkOptionByRawValue(name, value));
      }
      this.expandCategoryIfHiddenChecked();

      // Taille (en cm, direct)
      const tailleVal =
        params.get('filter.p.m.specs.taicyc') ||
        params.get('filter.v.m.taille_du_cycliste.liste') ||
        params.get('filter.p.v.m.specs.taicyc');
      const tailleInput = this.querySelector('input[name="taille"]');
      if (tailleVal && tailleInput) tailleInput.value = tailleVal.replace(/[^\d]/g, '');

      // Budget (bornes du filtre prix)
      const priceGte = params.get('filter.v.price.gte');
      const priceLte = params.get('filter.v.price.lte');
      const budgetMinInput = this.querySelector('input[name="budget_min"]');
      const budgetMaxInput = this.querySelector('input[name="budget_max"]');
      if (priceGte && budgetMinInput) {
        // `gte=0` est la borne par défaut de Shopify : on laisse le champ vide
        const cleaned = priceGte.replace(/[^\d]/g, '');
        if (parseInt(cleaned, 10) > 0) budgetMinInput.value = cleaned;
      }
      if (priceLte && budgetMaxInput) budgetMaxInput.value = priceLte.replace(/[^\d]/g, '');

      // Filtres dynamiques (marque + autres) : auto-ajout puis pré-sélection
      const available = this.getAvailableDynamicFilters();
      for (const key of filterKeys) {
        const filter = available.find((f) => f.id === key);
        if (!filter) continue;
        this.addDynamicFilter(filter);
        params.getAll(key).forEach((value) => this.checkOptionByRawValue(key, value));
      }
    }

    // Toujours en dernier : le pré-remplissage programmatique s'applique par-dessus
    // l'état de base (URL de collection), même sans aucun paramètre `filter.*`.
    this.applyPrefill();
  }

  // Catégorie : déplier si une valeur cochée est masquée derrière "Voir plus"
  expandCategoryIfHiddenChecked() {
    const catMs = this.querySelector('[data-multiselect="category"]');
    if (!catMs || !catMs.querySelector('.alerte-site-modal__button-group-btn.is-hidden input:checked')) return;
    catMs.classList.add('is-expanded');
    const moreBtn = catMs.querySelector('.alerte-site-modal__button-group-more');
    if (moreBtn) {
      moreBtn.dataset.expanded = 'true';
      moreBtn.textContent = 'Voir moins';
    }
  }

  // ---------------------------------------------------------------------------
  // Pré-remplissage programmatique (fiche produit vendue —
  // snippets/product-essentials-sold.liquid + assets/product-essentials-sold.js)
  // ---------------------------------------------------------------------------

  /**
   * Pose un pré-remplissage à appliquer à la PROCHAINE ouverture de la modale.
   * À appeler AVANT `xModal.open()` : l'ouverture déclenche loadDropdownValues()
   * (1re fois) ou resetForm() (suivantes), qui se terminent tous deux par
   * preselectFromCollectionFilters() → applyPrefill(). Posé après open(), le prefill
   * ne serait consommé qu'à l'ouverture d'après.
   * @param {{email?: string, type?: string, category?: string[]|string, marque?: string,
   *          budgetMin?: string|number, budgetMax?: string|number}} data
   */
  prefill(data) {
    this.prefillData = data || null;
  }

  applyPrefill() {
    const p = this.prefillData;
    if (!p) return;
    this.prefillData = null; // one-shot

    // Email : passer par l'événement `input` pour déclencher validateEmail() et
    // toggleMintFields() exactement comme une saisie clavier.
    const emailInput = this.querySelector('input[name="email"]');
    if (emailInput && p.email) {
      emailInput.value = p.email;
      emailInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Type & catégorie (button-groups)
    if (p.type) this.checkOptionByRawValue('type', p.type);
    const categories = Array.isArray(p.category) ? p.category : [p.category];
    categories.filter(Boolean).forEach((value) => this.checkOptionByRawValue('category', value));
    this.expandCategoryIfHiddenChecked();

    // Marque : filtre dynamique, ajouté seulement s'il est proposable (getAvailableDynamicFilters
    // exclut les filtres à valeurs groupées et ceux déjà ajoutés) ou déjà présent.
    if (p.marque) {
      const marqueId = AlertSiteModal.MARQUE_FILTER_ID;
      if (!this.addedFilters.has(marqueId)) {
        const filter = this.getAvailableDynamicFilters().find((f) => f.id === marqueId);
        if (filter) this.addDynamicFilter(filter);
      }
      if (this.addedFilters.has(marqueId)) this.checkOptionByRawValue(marqueId, p.marque);
    }

    // Bornes de budget : uniquement si le champ est encore vide (l'URL de collection
    // reste prioritaire quand elle en fournit une). La taille n'est jamais pré-remplie :
    // c'est le champ que l'utilisateur doit renseigner lui-même.
    const fillIfEmpty = (name, value) => {
      const input = this.querySelector(`input[name="${name}"]`);
      if (!input || input.value.trim()) return;
      const digits = String(value ?? '').replace(/[^\d]/g, '');
      if (digits) input.value = digits;
    };
    fillIfEmpty('budget_min', p.budgetMin);
    fillIfEmpty('budget_max', p.budgetMax);
  }

  checkOptionByRawValue(name, rawValue) {
    const ms = this.querySelector(`[data-multiselect="${name}"]`);
    if (!ms) return;
    const boxes = Array.from(ms.querySelectorAll('input[type="checkbox"]'));
    const wanted = String(rawValue).trim();
    // `data-raw-values` peut porter PLUSIEURS valeurs (`parseFilterInputValues` les
    // sérialise en `values.join(', ')`) : on compare chacune à la valeur cherchée.
    const valuesOf = (c) => (c.dataset.rawValues || c.value).split(',').map((v) => v.trim());
    // Correspondance exacte d'abord ; repli insensible à la casse (un metafield produit
    // « Électrique » face à un libellé de filtre « électrique », par exemple).
    const cb =
      boxes.find((c) => valuesOf(c).includes(wanted)) ||
      boxes.find((c) => valuesOf(c).some((v) => v.toLowerCase() === wanted.toLowerCase()));

    if (!cb || cb.checked) return;
    cb.checked = true;
    cb.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Déplie "Voir plus" si une valeur cochée (URL ou pré-remplissage trigger) est masquée
  // derrière — extrait de preselectFromCollectionFilters pour être réutilisé par
  // applyTriggerPrefill (une catégorie hors PRIMARY_CATEGORY_LABELS reste sinon cochée
  // mais invisible).
  revealCheckedHiddenCategory() {
    const catMs = this.querySelector('[data-multiselect="category"]');
    if (!catMs) return;
    if (!catMs.querySelector('.alerte-site-modal__button-group-btn.is-hidden input:checked')) return;
    catMs.classList.add('is-expanded');
    const moreBtn = catMs.querySelector('.alerte-site-modal__button-group-more');
    if (moreBtn) {
      moreBtn.dataset.expanded = 'true';
      moreBtn.textContent = 'Voir moins';
    }
  }

  // ---------------------------------------------------------------------------
  // Pré-remplissage depuis le déclencheur (ex. CTA "Me prévenir" d'un vélo vendu)
  // ---------------------------------------------------------------------------

  // Strictement opt-in : sans data-vvr-sold-* sur le trigger (cas des 7 autres surfaces qui
  // ouvrent cette modale), ce qui suit est un no-op — aucune des lectures ci-dessous ne trouve
  // de valeur. Les multiselects type/category sont déjà peuplés à ce point (cf. les deux points
  // d'appel dans loadDropdownValues et resetForm, mêmes emplacements que preselectFromCollectionFilters
  // — anti-pattern n°23 : ne jamais écrire dans un multiselect encore en chargement).
  applyTriggerPrefill() {
    const trigger = this.pendingPrefillTrigger;
    if (!trigger) return;

    const type = trigger.dataset.vvrSoldType;
    if (type) this.checkOptionByRawValue('type', type);

    // `velo.categorie_secondaire` est une LISTE (mesuré : list.single_line_text_field,
    // ex. ["VTT"]) : le Liquid la joint par virgules, on recoche chaque valeur.
    const category = trigger.dataset.vvrSoldCategory;
    if (category) {
      category
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
        .forEach((v) => this.checkOptionByRawValue('category', v));
    }
    this.revealCheckedHiddenCategory();

    // Taille du visiteur : on propose le milieu de la fourchette du vélo, jamais par-dessus
    // une saisie existante. Bornée par les mêmes limites que le champ lui-même.
    const taille = trigger.dataset.vvrSoldTaille;
    const tailleInput = this.querySelector('input[name="taille"]');
    if (taille && tailleInput && !tailleInput.value) {
      const cm = parseInt(taille, 10);
      const { min: tailleLow, max: tailleHigh } = AlertSiteModal.TAILLE_BOUNDS;
      if (!isNaN(cm) && cm >= tailleLow && cm <= tailleHigh) tailleInput.value = cm;
    }

    // Fourchette de ±20 % autour du prix du vélo vendu, arrondie à la dizaine. Chaque borne
    // est ramenée dans BUDGET_BOUNDS (sinon `validateForm` refuserait un champ que le visiteur
    // n'a pas touché) et ne remplace jamais une valeur déjà présente (ex. issue de
    // preselectFromCollectionFilters).
    const price = parseInt(trigger.dataset.vvrSoldBudgetMax, 10);
    const budgetMinInput = this.querySelector('input[name="budget_min"]');
    const budgetMaxInput = this.querySelector('input[name="budget_max"]');
    const { min: budgetLow, max: budgetHigh } = AlertSiteModal.BUDGET_BOUNDS;
    const roundTen = (v) => Math.round(v / 10) * 10;
    // Sous la borne basse même en haut de fourchette : aucune fourchette valide possible.
    if (isNaN(price) || roundTen(price * 1.2) < budgetLow) return;

    if (budgetMaxInput && !budgetMaxInput.value) {
      const budgetMinVal = budgetMinInput ? parseInt(budgetMinInput.value, 10) : NaN;
      const max = Math.min(roundTen(price * 1.2), budgetHigh);
      // `validateForm` refuse `budget_min > budget_max` : jamais sous un minimum déjà saisi.
      if (isNaN(budgetMinVal) || budgetMinVal <= max) budgetMaxInput.value = max;
    }

    if (budgetMinInput && !budgetMinInput.value) {
      const budgetMaxVal = budgetMaxInput ? parseInt(budgetMaxInput.value, 10) : NaN;
      const min = Math.max(roundTen(price * 0.8), budgetLow);
      if (isNaN(budgetMaxVal) || min <= budgetMaxVal) budgetMinInput.value = min;
    }
  }

  // ---------------------------------------------------------------------------
  // Multiselect dropdowns
  // ---------------------------------------------------------------------------

  initMultiselects() {
    this.querySelectorAll('[data-multiselect]:not([data-multiselect-buttons])').forEach((ms) => {
      this.bindMultiselectEvents(ms);
    });
    document.addEventListener('click', this.handleOutsideClick);
  }

  bindMultiselectEvents(ms) {
    const input = ms.querySelector('.alerte-site-modal__multiselect-input');
    const chevron = ms.querySelector('.alerte-site-modal__multiselect-chevron');
    if (!input) return;

    input.addEventListener('focus', () => this.openMultiselect(ms));
    input.addEventListener('input', () => this.filterMultiselectOptions(ms));
    input.addEventListener('click', () => this.openMultiselect(ms));

    if (chevron) {
      chevron.style.cursor = 'pointer';
      chevron.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleMultiselect(ms);
      });
    }
  }

  openMultiselect(ms) {
    const trigger = ms.querySelector('.alerte-site-modal__multiselect-trigger');
    const panel = ms.querySelector('.alerte-site-modal__multiselect-panel');
    const input = ms.querySelector('.alerte-site-modal__multiselect-input');
    if (!trigger || !panel) return;

    if (trigger.getAttribute('aria-expanded') === 'true') return;

    // Close all other panels first
    this.querySelectorAll('[data-multiselect]').forEach((other) => {
      if (other !== ms) this.closeMultiselect(other);
    });

    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;

    // Clear input for searching, show all options
    if (input) {
      input.value = '';
      input.placeholder = 'Rechercher\u2026';
    }
    const list = ms.querySelector('.alerte-site-modal__multiselect-options');
    if (list) {
      list.querySelectorAll('.alerte-site-modal__multiselect-option').forEach((li) => {
        li.style.display = '';
      });
    }
  }

  closeMultiselect(ms) {
    const trigger = ms.querySelector('.alerte-site-modal__multiselect-trigger');
    const panel = ms.querySelector('.alerte-site-modal__multiselect-panel');
    if (!trigger || !panel) return;

    trigger.setAttribute('aria-expanded', 'false');
    panel.hidden = true;

    this.updateMultiselectInputDisplay(ms);

    // Reset option visibility
    const list = ms.querySelector('.alerte-site-modal__multiselect-options');
    if (list) {
      list.querySelectorAll('.alerte-site-modal__multiselect-option').forEach((li) => {
        li.style.display = '';
      });
    }
  }

  toggleMultiselect(ms) {
    const trigger = ms.querySelector('.alerte-site-modal__multiselect-trigger');
    if (!trigger) return;
    if (trigger.getAttribute('aria-expanded') === 'true') {
      this.closeMultiselect(ms);
    } else {
      this.openMultiselect(ms);
      const input = ms.querySelector('.alerte-site-modal__multiselect-input');
      if (input) input.focus();
    }
  }

  filterMultiselectOptions(ms) {
    const input = ms.querySelector('.alerte-site-modal__multiselect-input');
    const list = ms.querySelector('.alerte-site-modal__multiselect-options');
    if (!input || !list) return;

    const query = input.value.toLowerCase().trim();
    list.querySelectorAll('.alerte-site-modal__multiselect-option').forEach((li) => {
      const text = li.textContent.toLowerCase();
      li.style.display = text.includes(query) ? '' : 'none';
    });
  }

  updateMultiselectInputDisplay(ms) {
    const input = ms.querySelector('.alerte-site-modal__multiselect-input');
    const trigger = ms.querySelector('.alerte-site-modal__multiselect-trigger');
    if (!input) return;

    const checked = ms.querySelectorAll('input[type="checkbox"]:checked');
    const placeholder = input.dataset.placeholder;

    if (checked.length === 0) {
      input.value = '';
      input.placeholder = placeholder;
      if (trigger) trigger.setAttribute('data-empty', 'true');
    } else if (checked.length <= 2) {
      input.value = Array.from(checked)
        .map((cb) => cb.value)
        .join(', ');
      input.placeholder = placeholder;
      if (trigger) trigger.setAttribute('data-empty', 'false');
    } else {
      input.value = `${checked.length} sélectionnés`;
      input.placeholder = placeholder;
      if (trigger) trigger.setAttribute('data-empty', 'false');
    }
  }

  handleOutsideClick(event) {
    this.querySelectorAll('[data-multiselect]').forEach((ms) => {
      if (!ms.contains(event.target)) {
        this.closeMultiselect(ms);
      }
    });
  }

  populateMultiselect(name, values) {
    const ms = this.querySelector(`[data-multiselect="${name}"]`);
    if (!ms) return;
    const list = ms.querySelector('.alerte-site-modal__multiselect-options');
    if (!list) return;
    list.innerHTML = '';

    for (const item of values) {
      const label = typeof item === 'string' ? item : item.label;
      const rawValues = typeof item === 'string' ? item : item.rawValues || item.label;

      const li = document.createElement('li');
      li.className = 'alerte-site-modal__multiselect-option';
      li.innerHTML = `<label><input type="checkbox" value="${label}" data-raw-values="${rawValues}" name="${name}"><span>${label}</span></label>`;
      const checkbox = li.querySelector('input[type="checkbox"]');
      checkbox.addEventListener('change', () => this.updateMultiselectLabel(name));
      list.appendChild(li);
    }
  }

  populateButtonGroup(name, values, options = {}) {
    const ms = this.querySelector(`[data-multiselect="${name}"]`);
    if (!ms) return;
    const container = ms.querySelector('.alerte-site-modal__button-group-options');
    if (!container) return;
    container.innerHTML = '';

    const labelOf = (item) => (typeof item === 'string' ? item : item.label);

    // Réordonner pour placer les valeurs principales en tête (masquer le reste)
    let ordered = values;
    let visibleCount = values.length;
    if (options.primaryLabels) {
      const primaries = options.primaryLabels
        .map((p) => values.find((v) => labelOf(v).toUpperCase() === p.toUpperCase()))
        .filter(Boolean);
      const rest = values.filter((v) => !primaries.includes(v));
      ordered = [...primaries, ...rest];
      visibleCount = primaries.length;
    }

    ordered.forEach((item, index) => {
      const label = labelOf(item);
      const rawValues = typeof item === 'string' ? item : item.rawValues || item.label;

      const btn = document.createElement('label');
      btn.className = 'alerte-site-modal__button-group-btn';
      if (index >= visibleCount) btn.classList.add('is-hidden');
      btn.innerHTML = `<input type="checkbox" value="${label}" data-raw-values="${rawValues}" name="${name}" hidden><span>${label}</span>`;
      btn.querySelector('input').addEventListener('change', (e) => {
        btn.classList.toggle('is-selected', e.target.checked);
        const parentMs = btn.closest('[data-multiselect]');
        if (parentMs) {
          parentMs.classList.remove('error');
          const errorEl = this.querySelector(`#${parentMs.dataset.multiselect}-error`);
          if (errorEl) {
            errorEl.classList.remove('show');
            errorEl.textContent = '';
          }
        }
      });
      container.appendChild(btn);
    });

    if (ordered.length > visibleCount) {
      this.addButtonGroupToggle(ms, container);
    }
  }

  addButtonGroupToggle(ms, container) {
    ms.classList.add('alerte-site-modal__button-group--has-more');

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'alerte-site-modal__button-group-more';
    toggle.dataset.expanded = 'false';
    toggle.textContent = 'Voir plus';
    toggle.addEventListener('click', () => {
      const expanded = toggle.dataset.expanded === 'true';
      toggle.dataset.expanded = expanded ? 'false' : 'true';
      toggle.textContent = expanded ? 'Voir plus' : 'Voir moins';
      ms.classList.toggle('is-expanded', !expanded);
    });
    container.after(toggle);
  }

  updateMultiselectLabel(name) {
    const ms = this.querySelector(`[data-multiselect="${name}"]`);
    if (!ms) return;

    // Button groups don't need label/badge updates
    if (ms.hasAttribute('data-multiselect-buttons')) return;

    const trigger = ms.querySelector('.alerte-site-modal__multiselect-trigger');
    const checked = ms.querySelectorAll('input[type="checkbox"]:checked');
    if (trigger) {
      trigger.setAttribute('data-empty', checked.length === 0 ? 'true' : 'false');
    }

    // Only update input text if panel is closed (user is not mid-search)
    const isOpen = trigger?.getAttribute('aria-expanded') === 'true';
    if (!isOpen) {
      this.updateMultiselectInputDisplay(ms);
    }

    this.renderBadges(name);

    // Le libellé/visibilité du bouton "Ajouter/Masquer" dépend des filtres vides → resync
    this.updateAddFilterButton();
  }

  renderBadges(name) {
    const ms = this.querySelector(`[data-multiselect="${name}"]`);
    if (!ms) return;

    const formGroup = ms.closest('.alerte-site-modal__form-group');
    if (!formGroup) return;

    let container = formGroup.querySelector('.alerte-site-modal__multiselect-badges');
    const checked = ms.querySelectorAll('input[type="checkbox"]:checked');

    if (checked.length === 0) {
      if (container) container.remove();
      return;
    }

    if (!container) {
      container = document.createElement('div');
      container.className = 'alerte-site-modal__multiselect-badges';
      ms.after(container);
    }

    container.innerHTML = '';

    checked.forEach((cb) => {
      const badge = document.createElement('button');
      badge.type = 'button';
      badge.className = 'alerte-site-modal__multiselect-badge';
      badge.innerHTML = `<span>${cb.value}</span><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
      badge.addEventListener('click', () => {
        cb.checked = false;
        this.updateMultiselectLabel(name);
      });
      container.appendChild(badge);
    });
  }

  getMultiselectValues(name) {
    const ms = this.querySelector(`[data-multiselect="${name}"]`);
    if (!ms) return '';
    const checked = ms.querySelectorAll('input[type="checkbox"]:checked');
    return Array.from(checked)
      .map((cb) => cb.dataset.rawValues || cb.value)
      .join(', ');
  }

  // ---------------------------------------------------------------------------
  // View toggling
  // ---------------------------------------------------------------------------

  showSubmitError() {
    const errorEl = this.querySelector('#submit-error');
    if (errorEl) {
      errorEl.textContent = this.dataset.errorMessage || 'Une erreur est survenue. Veuillez réessayer.';
      errorEl.classList.add('show');
    }
  }

  showSuccess() {
    if (this.viewForm) this.viewForm.style.display = 'none';
    if (this.viewSuccess) this.viewSuccess.style.display = '';

    const successElement = this.viewSuccess?.querySelector('.alerte-site-modal__success');
    if (successElement) successElement.classList.add('animate-in');
  }

  showForm() {
    if (this.viewSuccess) this.viewSuccess.style.display = 'none';
    if (this.viewForm) this.viewForm.style.display = '';
  }

  resetForm() {
    if (this.form) this.form.reset();
    this.submitted = false;

    // Clear validation states
    this.querySelectorAll('.error').forEach((el) => el.classList.remove('error'));
    this.querySelectorAll('.alerte-site-modal__error.show').forEach((el) => {
      el.classList.remove('show');
      el.textContent = '';
    });

    // Reset dropdown multiselects
    this.querySelectorAll('[data-multiselect]:not([data-multiselect-buttons])').forEach((ms) => {
      const name = ms.dataset.multiselect;
      ms.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = false;
      });
      this.closeMultiselect(ms);
      this.updateMultiselectLabel(name);
    });

    // Reset button groups
    this.querySelectorAll('[data-multiselect-buttons]').forEach((ms) => {
      ms.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = false;
      });
      ms.querySelectorAll('.alerte-site-modal__button-group-btn').forEach((btn) => {
        btn.classList.remove('is-selected');
      });
      // Re-replier le groupe (si "Voir plus" avait été déplié)
      ms.classList.remove('is-expanded');
      const moreBtn = ms.querySelector('.alerte-site-modal__button-group-more');
      if (moreBtn) {
        moreBtn.dataset.expanded = 'false';
        moreBtn.textContent = 'Voir plus';
      }
    });

    // Clear badges
    this.querySelectorAll('.alerte-site-modal__multiselect-badges').forEach((el) => el.remove());

    // Remove dynamic filters
    this.querySelectorAll('[data-dynamic-filter]').forEach((el) => el.remove());
    this.addedFilters.clear();
    this.updateAddFilterButton();

    const successElement = this.viewSuccess?.querySelector('.alerte-site-modal__success');
    if (successElement) successElement.classList.remove('animate-in');

    // Hide MINT internal fields
    const mintFields = this.querySelector('[data-mint-fields]');
    if (mintFields) mintFields.style.display = 'none';

    // Ré-appliquer les filtres de la collection comme état de base
    this.preselectFromCollectionFilters();

    // Ré-appliquer le pré-remplissage du trigger courant (opt-in, cf. handleModalOpened)
    this.applyTriggerPrefill();

    this.showForm();
  }
}

customElements.define('alerte-site-modal', AlertSiteModal);
